import { AUDIENCE_FOR_ROLE, type Audience } from '@lb/domain'

import { useSessionContext } from '../app/providers/session'

/**
 * The viewer's audience, for the two reads that ask *which vocabulary* rather
 * than *what may I see*.
 *
 * ── Why this exists as a hook and not a per-screen decision ───────────────
 * `useDestinations` and `useOnboardingQuestionnaire` both take a required
 * audience since 2026-08-19, and there are twelve call sites between them. A
 * screen that picks its own would be a screen that can pick differently, and
 * the failure is invisible from the inside: a parent seeing *Clinical
 * integration* in their filter row looks like a content problem, not a
 * parameter problem.
 *
 * ── `undefined` before the session resolves, deliberately ─────────────────
 * Both hooks hold their query on `undefined` rather than falling back to
 * PARENT. A wrong first answer would be cached under a 30-minute `staleTime`
 * and served to a practitioner for half an hour.
 *
 * ── ORG_ADMIN maps to ORG, and that is knowingly thin ─────────────────────
 * `AUDIENCE_FOR_ROLE` is the backend's own `viewerAudience` mapping, and this
 * uses it unchanged rather than routing org admins onto the Pro sets. That
 * keeps one mapping in the product, at a known cost already recorded in
 * `nav.ts`: nothing carries the ORG audience, so an org admin's questionnaire
 * comes back empty and their destinations are the unrestricted six. Empty is
 * the honest answer — the alternative is asking them a practitioner's
 * questions because a parent's were worse.
 */
export function useViewerAudience(): Audience | undefined {
  const { viewer } = useSessionContext()
  const role = viewer?.session.platformRole
  return role ? AUDIENCE_FOR_ROLE[role] : undefined
}
